import { useEffect, useMemo, useState } from "react";
import { Server, Layers, MapPin, Boxes, Gauge, Cpu, MemoryStick, HardDrive, Lightbulb } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ToolHeader } from "@/components/common/ToolHeader";
import { useCalculation } from "@/components/common/useCalculation";
import { HOSTING_DEFAULT, computeFleet, basisOf } from "@/lib/engines/hosting";
import { fmtNum, fmtRp, fmtRpShort } from "@/lib/format";
import { KpiCard } from "@/components/common/Cards";
import InventoryPanel from "@/components/hosting/InventoryPanel";
import LocationPanel from "@/components/hosting/LocationPanel";
import PackagesPanel from "@/components/hosting/PackagesPanel";
import CapacityPanel from "@/components/hosting/CapacityPanel";
import RecommendPanel from "@/components/hosting/RecommendPanel";

export default function HostingToolPage() {
  const calc = useCalculation("hosting", HOSTING_DEFAULT, "Perhitungan Hosting/VPS");
  const { data, setData, readOnly, loading } = calc;
  const [servers, setServers] = useState(null);

  const loadServers = () =>
    api
      .get("/servers")
      .then(({ data }) => setServers(data))
      .catch(() => setServers([]));

  useEffect(() => {
    loadServers();
  }, []);

  const fleet = useMemo(() => computeFleet(servers || [], data), [servers, data]);
  const b = useMemo(() => basisOf(fleet, data.basis), [fleet, data.basis]);

  const update = (key) => (updater) =>
    setData((d) => ({ ...d, [key]: typeof updater === "function" ? updater(d[key]) : updater }));

  const setParams = (updater) => setData((d) => ({ ...d, params: typeof updater === "function" ? updater(d.params) : updater }));
  const setLocations = (updater) => setData((d) => ({ ...d, locations: typeof updater === "function" ? updater(d.locations || {}) : updater }));
  const setPackages = (updater) => setData((d) => ({ ...d, packages: typeof updater === "function" ? updater(d.packages) : updater }));

  const summary = () => ({
    headline: `${fleet.compute.length} server compute · total ${fmtRpShort(fleet.totalMonthly)}/bulan`,
    values: {
      servers: fleet.selectedCount,
      vcpu: Math.round(fleet.pool.cap.vcpu),
      ram: Math.round(fleet.pool.cap.ram),
      disk: Math.round(fleet.pool.cap.disk),
      monthlyCost: Math.round(fleet.totalMonthly),
      packages: data.packages.length,
    },
  });

  const aiContext = () => ({
    servers: (servers || []).map((s) => ({
      hostname: s.hostname,
      location: s.location,
      role: s.role,
      cores: s.cores,
      threads: s.threads,
      ram_gb: s.ram_gb,
      storage_tb: s.storage_tb,
      allocated_vms: s.allocated?.count || 0,
      purchase_price: s.purchase_price,
      power_watt: s.power_watt,
      ip_count: s.ip_count,
    })),
    params: data.params,
    locations: data.locations,
    pool: {
      vcpu: Math.round(fleet.pool.cap.vcpu),
      ram_gb: Math.round(fleet.pool.cap.ram),
      disk_gb: Math.round(fleet.pool.cap.disk),
      allocated: fleet.pool.alloc,
      monthlyCost: Math.round(fleet.totalMonthly),
      unitCost: {
        per_vcpu: Math.round(fleet.pool.unit.cpu),
        per_gb_ram: Math.round(fleet.pool.unit.ram),
        per_gb_disk: Math.round(fleet.pool.unit.disk),
      },
    },
    packages: data.packages,
  });

  return (
    <div className="max-w-[1600px] mx-auto">
      <ToolHeader
        icon={Server}
        eyebrow="Tool 02 · Hosting / VPS"
        title="COGS Hosting / VPS"
        description="Hitung COGS paket berdasarkan biaya hardware, listrik, DC/colocation, bandwidth dan IP. Rekomendasi paket + kapasitas per server."
        calc={calc}
        tool="hosting"
        getSummary={summary}
        getAiContext={aiContext}
      />

      {servers === null || loading ? (
        <div className="space-y-3" data-testid="hosting-loading">
          <Skeleton className="h-20 bg-[hsl(220_10%_11%)]" />
          <Skeleton className="h-80 bg-[hsl(220_10%_11%)]" />
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-5">
            <KpiCard label="Server compute" value={fmtNum(fleet.compute.length)} sub={`${fleet.selectedCount} dipilih · ${fleet.support.length} support`} icon={Server} testid="hosting-kpi-servers" />
            <KpiCard label="vCPU usable" value={fmtNum(Math.round(fleet.pool.cap.vcpu))} sub={`terpakai ${fmtNum(fleet.pool.alloc.vcpu)}`} icon={Cpu} testid="hosting-kpi-vcpu" />
            <KpiCard label="RAM usable" value={`${fmtNum(Math.round(fleet.pool.cap.ram))} GB`} sub={`terpakai ${fmtNum(fleet.pool.alloc.ram)} GB`} icon={MemoryStick} testid="hosting-kpi-ram" />
            <KpiCard label="Disk usable" value={`${fmtNum(Math.round(fleet.pool.cap.disk / 1000))} TB`} sub={`terpakai ${fmtNum(Math.round(fleet.pool.alloc.disk / 1000))} TB`} icon={HardDrive} testid="hosting-kpi-disk" />
            <KpiCard label="Biaya operasional" value={fmtRpShort(fleet.totalMonthly)} sub="per bulan (semua server)" icon={Gauge} accent testid="hosting-kpi-cost" />
          </div>

          <Tabs value={data.tab} onValueChange={(v) => setData((d) => ({ ...d, tab: v }))}>
            <TabsList className="bg-[hsl(220_12%_8%)] border border-border h-auto p-1 flex-wrap" data-testid="hosting-tabs">
              <TabsTrigger value="inventory" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="hosting-tab-inventory">
                <Server className="h-4 w-4 mr-1.5" /> Inventory
              </TabsTrigger>
              <TabsTrigger value="location" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="hosting-tab-location">
                <MapPin className="h-4 w-4 mr-1.5" /> Biaya Lokasi
              </TabsTrigger>
              <TabsTrigger value="packages" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="hosting-tab-packages">
                <Boxes className="h-4 w-4 mr-1.5" /> Paket
              </TabsTrigger>
              <TabsTrigger value="capacity" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="hosting-tab-capacity">
                <Layers className="h-4 w-4 mr-1.5" /> Kapasitas
              </TabsTrigger>
              <TabsTrigger value="recommend" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="hosting-tab-recommend">
                <Lightbulb className="h-4 w-4 mr-1.5" /> Rekomendasi
              </TabsTrigger>
            </TabsList>
            <TabsContent value="inventory" className="mt-5">
              <InventoryPanel servers={servers} data={data} setData={setData} setParams={setParams} readOnly={readOnly} fleet={fleet} onReload={loadServers} />
            </TabsContent>
            <TabsContent value="location" className="mt-5">
              <LocationPanel servers={servers} locations={data.locations} setLocations={setLocations} readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="packages" className="mt-5">
              <PackagesPanel
                packages={data.packages}
                setPackages={setPackages}
                params={data.params}
                setParams={setParams}
                fleet={fleet}
                basis={data.basis}
                setBasis={(v) => setData((d) => ({ ...d, basis: v }))}
                basisResolved={b}
                readOnly={readOnly}
              />
            </TabsContent>
            <TabsContent value="capacity" className="mt-5">
              <CapacityPanel
                fleet={fleet}
                packages={data.packages}
                capacityPackageId={data.capacityPackageId}
                setCapacityPackageId={(v) => setData((d) => ({ ...d, capacityPackageId: v }))}
              />
            </TabsContent>
            <TabsContent value="recommend" className="mt-5">
              <RecommendPanel basis={b} params={data.params} fleet={fleet} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
